import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { useTVAuth } from './TVContext';
import { focusFirst, useDpadNavigation } from './focus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 3000;

const TVPair = () => {
  const navigate = useNavigate();
  const { setTVToken } = useTVAuth();
  const [code, setCode] = useState('');
  const [pairUrl, setPairUrl] = useState('');
  const [status, setStatus] = useState('loading'); // loading | ready | expired | error
  const pollRef = useRef(null);

  useDpadNavigation(() => { /* no back from pair */ });

  const generate = async () => {
    setStatus('loading');
    try {
      const res = await axios.post(`${API}/tv/generate-code`);
      setCode(res.data.code);
      setPairUrl(res.data.pair_url);
      setStatus('ready');
    } catch (e) {
      setStatus('error');
    }
  };

  useEffect(() => { generate(); focusFirst(); }, []);

  useEffect(() => {
    if (!code || status !== 'ready') return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/tv/check-code/${code}`);
        if (res.data.status === 'paired' && res.data.session_token) {
          clearInterval(pollRef.current);
          setTVToken(res.data.session_token, res.data.user);
          navigate('/tv/browse');
        } else if (res.data.status === 'expired') {
          clearInterval(pollRef.current);
          setStatus('expired');
        }
      } catch (e) { /* poll continues; transient network errors logged in dev */ 
        if (process.env.NODE_ENV === 'development') console.debug('TV pair poll error:', e);
      }
    }, POLL_MS);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [code, status, navigate, setTVToken]);

  return (
    <div className="tv-root" data-testid="tv-pair-screen">
      <div className="tv-header">
        <div className="tv-brand">PULSE MEDIA</div>
        <div className="tv-user">TV Sign In</div>
      </div>
      <div className="tv-pair-wrap">
        <div className="tv-pair-left">
          <h1>Sign in to Pulse Media on your TV</h1>
          <p>On your phone or computer, visit the URL below and enter this code. It expires in 10 minutes.</p>
          {status === 'ready' && (
            <>
              <div className="tv-code" data-testid="tv-pair-code">{code}</div>
              <div className="tv-pair-url" data-testid="tv-pair-url">{pairUrl.replace(/^https?:\/\//, '')}</div>
            </>
          )}
          {status === 'expired' && (
            <div style={{ marginTop: 28 }}>
              <div style={{ color: '#ff8b8b', fontSize: 20, marginBottom: 16 }}>This code expired.</div>
              <button className="tv-btn tv-focusable" data-testid="tv-pair-refresh" onClick={generate}>Get a new code</button>
            </div>
          )}
          {status === 'error' && (
            <div style={{ marginTop: 28 }}>
              <div style={{ color: '#ff8b8b', fontSize: 20, marginBottom: 16 }}>Could not reach Pulse Media. Check your internet connection.</div>
              <button className="tv-btn tv-focusable" onClick={generate}>Try again</button>
            </div>
          )}
          <div style={{ marginTop: 36, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <button className="tv-btn tv-focusable" data-testid="tv-pair-email-signin" onClick={() => navigate('/tv/signin')}>
              Sign in with email
            </button>
            <button className="tv-btn tv-btn-ghost tv-focusable" data-testid="tv-pair-email-signup" onClick={() => navigate('/tv/signup')}>
              Create account
            </button>
            <button className="tv-btn tv-btn-ghost tv-focusable" data-testid="tv-pair-browse-guest" onClick={() => navigate('/tv/browse')}>
              Continue as guest
            </button>
          </div>
        </div>
        <div className="tv-pair-right">
          {status === 'ready' && pairUrl && (
            <div className="tv-qr-box" data-testid="tv-pair-qr">
              <QRCodeSVG value={pairUrl} size={280} level="M" includeMargin={false} />
            </div>
          )}
          {status === 'loading' && <div style={{ color: '#a8b8cf', fontSize: 20 }}>Generating code…</div>}
        </div>
      </div>
    </div>
  );
};

export default TVPair;
